import { useMemo } from 'react';
import { useTranslate } from '../../application/i18n/LocaleProvider';
import { carName } from '../../domain/cars/carCatalogue';
import { famousOwners, ownerInitials } from '../../domain/cars/carOwners';

/**
 * The people attached to this car (SPEC-008).
 *
 * Three rows, each a face, a name, a job, a chip and two short paragraphs. It
 * sits between the prose and the gallery because it is a footnote to what the
 * car IS and comes before what it LOOKS like — and because putting faces above
 * the description would push the description below a screenful of them.
 *
 * THE CHIP IS THE POINT OF THE COMPONENT, not decoration on it. This band's
 * subject is real and usually living people, and the claim it makes about each
 * one has to be legible at a glance: Nigel Mansell owned an F40, Ayrton Senna
 * broke an NSX prototype's chassis for Honda, Vin Diesel drove an RX-7 through a
 * film set. Without the chip a reader reads every row as ownership, which for
 * two thirds of these people would be something this product had invented about
 * them. So the chip is drawn next to the occupation rather than tucked under the
 * story, and it is never the thing that gets dropped at a narrow width.
 *
 * A list of rows and not a rail, unlike the gallery. Nine pictures of one car
 * are a contact sheet and belong in a strip; three people with a paragraph each
 * are a list, and a horizontally scrolling one would both hide two thirds of the
 * text and put a sideways gesture on the one screen where a sideways gesture
 * already deals the next car (SPEC-004 D-8).
 */

/**
 * One person's picture, or the plate that stands in for it.
 *
 * There is no portrait artwork in this repository yet and the band ships anyway:
 * a person without one gets their initials set as a display figure over the
 * car's own livery wash. That is a DESIGNED state, not a fallback — it reads as
 * a plate somebody drew, which is the one thing a broken `<img>` can never do.
 *
 * Decorative either way. The name is on the very next line, so a portrait that
 * described itself would make a screen reader say "Nigel Mansell, Nigel
 * Mansell". This is the opposite of `CarGallery`, where the take IS the
 * information and the `alt` is the only place it exists.
 */
function OwnerPortrait({ owner }) {
    if (!owner.portrait) {
        return (
            <p className="owner__monogram" aria-hidden="true">
                {ownerInitials(owner.name)}
            </p>
        );
    }

    return (
        <img
            className="owner__portrait"
            src={owner.portrait.src}
            srcSet={owner.portrait.srcSet}
            sizes="64px"
            alt=""
            /* Below the fold on every phone, always — this band is beneath the
               card, the specification, two charts and four bands of prose. */
            loading="lazy"
            decoding="async"
        />
    );
}

export default function CarOwners({ car }) {
    const t = useTranslate();
    // Memoised on the car, and that is not a micro-optimisation. This screen
    // re-renders on EVERY pointermove of a deck swipe — the gesture feeds
    // `--deck-progress` on the article — so an unmemoised join would re-run its
    // drops per frame, and a catalogue with one blank `story` would fire a
    // `console.warn` per frame during the one interaction on this screen that is
    // performance-sensitive. `joinCatalogue` reports its drops once, at fetch
    // time; this reports them once per car, which is the same promise.
    const owners = useMemo(() => famousOwners(car), [car]);

    // Absent rather than empty, like the gallery and the acceleration band: a
    // heading reading "Most famous owners" over nothing says something about the
    // car that is not true.
    if (owners.length === 0) {
        return null;
    }

    return (
        <section className="car-owners" aria-labelledby="car-owners-title">
            <header className="section-heading">
                <h2 className="section-heading__text" id="car-owners-title">
                    {t('cars.section.owners')}
                </h2>
                <span className="section-heading__rule" aria-hidden="true" />
            </header>

            {/* Unordered, deliberately. The order is editorial — the Viper leads
                with the man who asked for it to be built — but it is not a
                ranking, and an `<ol>` would have a screen reader announce one,
                putting Eric Clapton second at his F40 rather than beside it. */}
            <ul
                className="owner-list"
                aria-label={t('a11y.owners', { car: carName(car) })}
                /* Explicit, because `list-style: none` makes WebKit drop the
                   list role — and with it this label and the count. iOS Safari
                   is this product's primary target and the one platform with a
                   simulator lane of its own, so losing "list, 3 items" there
                   would read the three people as one run of text. */
                role="list"
            >
                {owners.map((owner) => (
                    <li className="owner" key={owner.id}>
                        <div className="owner__plate">
                            <OwnerPortrait owner={owner} />
                        </div>

                        <div className="owner__identity">
                            {/* A heading rather than a paragraph, at the level
                                under the band's own: three people are three
                                subsections of "Most famous owners", and this is
                                what lets somebody reading by ear jump between
                                them instead of listening through two paragraphs
                                to reach the next name. */}
                            <h3 className="owner__name">{owner.name}</h3>
                            <p className="owner__occupation">{owner.occupation}</p>
                            {/* Read AFTER the name and the job and before the
                                story, which is the order the claim makes sense
                                in: who, what they did, then how they touched
                                this car. */}
                            <p className="owner__connection">
                                {t(`cars.owners.connection.${owner.connection}`)}
                            </p>
                        </div>

                        <p className="owner__story">{owner.story}</p>
                        <p className="owner__background">{owner.background}</p>
                    </li>
                ))}
            </ul>
        </section>
    );
}
